"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { useRouter, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { safeRedirect } from "@/lib/safe-redirect";
import { useTheme } from "next-themes";
import { Loader2Icon } from "lucide-react";
import { cn } from "@/lib/utils";
import { BRAND_LOGO_CLASS } from "@/components/branding-context";
import { LanguageSwitcher } from "@/components/language-switcher";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { authApi, brandingApi } from "@/lib/api";
import { type PublicBranding } from "@/lib/types";
import { landingForRole } from "@/lib/roles";

/**
 * Resolves the agency subdomain for the login page.
 *
 * In production, the agency reaches its branded login at a wildcard subdomain
 * (e.g. `foo.smm-test.acciolytix.com` → "foo"); this REQUIRES wildcard DNS
 * (`*.smm-test.acciolytix.com`) pointing at the app so the subdomain host
 * resolves. The first label is the agency slug, provided the host has more
 * labels than the apex (>2 labels, ignoring `www`) and is not a bare
 * IP/localhost.
 *
 * For dev (and any environment without wildcard DNS) a `?brand=<subdomain>`
 * query override is also accepted so the branded login can be exercised at
 * `localhost:3000/login?brand=foo`.
 */
function resolveSubdomain(host: string, brandOverride: string | null): string | null {
  if (brandOverride && brandOverride.trim()) return brandOverride.trim();

  const hostname = host.split(":")[0]; // strip any :port
  if (!hostname || hostname === "localhost") return null;
  // Bare IPv4 has no subdomain to read.
  if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) return null;

  const labels = hostname.split(".").filter(Boolean);
  // Need at least sub.apex.tld (3 labels) to have a subdomain.
  if (labels.length < 3) return null;
  const first = labels[0];
  if (first === "www") return null;
  return first;
}

export function LoginForm({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const t = useTranslations("auth");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [branding, setBranding] = useState<PublicBranding | null>(null);

  // Detect the agency subdomain (or ?brand= override) and load its public
  // branding so a client reaching the agency's subdomain sees agency-branded
  // login. A 404 (unknown subdomain) silently keeps the default brand.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sub = resolveSubdomain(
      window.location.host,
      searchParams.get("brand"),
    );
    if (!sub) return;
    let active = true;
    void brandingApi
      .bySubdomain(sub)
      .then((res) => {
        if (active) setBranding(res.branding);
      })
      .catch(() => {
        // Unknown subdomain / not reachable → default brand.
      });
    return () => {
      active = false;
    };
  }, [searchParams]);

  // Apply the agency primary color to the login card's accents.
  const accentStyle =
    branding?.primary_color &&
    /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(branding.primary_color)
      ? ({ "--primary": branding.primary_color } as React.CSSProperties)
      : undefined;

  const brandName = branding?.brand_name?.trim() || "Accio SMM";
  // Prefer the dark-theme logo in dark mode; fall back to the light logo.
  const { resolvedTheme } = useTheme();
  const loginLogo =
    (resolvedTheme === "dark" ? branding?.logo_dark_url?.trim() : "") ||
    branding?.logo_url?.trim() ||
    "";

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await authApi.login(email, password);
      if (res["2fa_required"]) {
        setError(t("twoFactorNotSupported"));
        setSubmitting(false);
        return;
      }
      const redirect = safeRedirect(searchParams.get("redirect"));
      const dest = redirect || landingForRole(res.user?.role ?? "client_member");
      router.replace(dest);
      // Keep the spinner until the navigation commits.
    } catch (err) {
      setError(err instanceof Error ? err.message : t("loginFailed"));
      setSubmitting(false);
    }
  }

  return (
    <div
      className={cn("flex flex-col gap-6", className)}
      style={accentStyle}
      {...props}
    >
      <Card>
        <CardHeader>
          {loginLogo && (
            <Image
              src={loginLogo}
              alt={brandName}
              width={200}
              height={200}
              unoptimized
              className={cn("mb-2", BRAND_LOGO_CLASS)}
            />
          )}
          <CardTitle>{t("loginTitle")}</CardTitle>
          <CardDescription>
            {t("loginDescription", { brand: brandName })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertTitle>{t("signInFailed")}</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <form method="post" noValidate onSubmit={handleSubmit}>
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="email">{t("email")}</FieldLabel>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="m@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={submitting}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="password">{t("password")}</FieldLabel>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={submitting}
                />
              </Field>
              <Field>
                <Button type="submit" disabled={submitting}>
                  {submitting && (
                    <Loader2Icon className="size-4 animate-spin" aria-hidden />
                  )}
                  {submitting ? t("signingIn") : t("login")}
                </Button>
                <FieldDescription className="text-center">
                  {branding?.support_email
                    ? t("needHelpContact", { email: branding.support_email })
                    : t("needAccount")}
                </FieldDescription>
              </Field>
            </FieldGroup>
          </form>
        </CardContent>
      </Card>
      <LanguageSwitcher variant="inline" />
    </div>
  );
}
